import { ComponentType } from 'react';

import { SUPPORTED_CHECKOUT_FLOW_TYPES } from '@portal/types/offer';
import dynamic from 'next/dynamic';
import { ContentBlockProps } from 'shared/components/ContentBlocks/common';

const ExpandableList = dynamic(
  () =>
    import('shared/components/ContentBlocks/ExpandableList/ExpandableList').then(
      (i) => i.ExpandableList
    ),
  {}
);
const ImageAndText = dynamic(
  () =>
    import('shared/components/ContentBlocks/ImageAndText/ImageAndText').then((i) => i.ImageAndText),
  {}
);
const ImageGallery = dynamic(
  () =>
    import('shared/components/ContentBlocks/ImageGallery/ImageGallery').then((i) => i.ImageGallery),
  {}
);
const ListAndImage = dynamic(
  () =>
    import('shared/components/ContentBlocks/ListAndImage/ListAndImage').then((i) => i.ListAndImage),
  {}
);
const OfferList = dynamic(
  () =>
    import('shared/components/ContentBlocks/Offer/OfferList/OfferList').then((i) => i.OfferList),
  {}
);
const OfferPrice = dynamic(
  () =>
    import('@portal/shared/components/ContentBlocks/Offer/OfferPrice/OfferPrice').then(
      (i) => i.OfferPrice
    ),
  {}
);
const OfferAnchor = dynamic(
  () =>
    import('@portal/shared/components/ContentBlocks/Offer/OfferAnchor/OfferAnchor').then(
      (i) => i.OfferAnchor
    ),
  {}
);
const OfferBuildBundle = dynamic(
  () =>
    import('shared/components/ContentBlocks/Offer/BuildBundle/BuildBundle').then(
      (i) => i.BuildBundle
    ),
  {}
);

/**
 * Content block sections in this list are able to display some partial data if
 * the page has a valid Offer associated with it.
 */
const OFFER_CONTENT_BLOCK_NAMES = [
  'Offer-list-builder',
  'Offer-price-builder',
  'Offer Anchor'
] as const;

export const isOfferContentBlockName = (
  name: string
): name is (typeof OFFER_CONTENT_BLOCK_NAMES)[number] =>
  OFFER_CONTENT_BLOCK_NAMES.includes(name as any);

const CONTENT_BLOCK_NAMES = [
  'Image & Text',
  'List & Image',
  'Image Gallery',
  'Expandable List',
  ...SUPPORTED_CHECKOUT_FLOW_TYPES.map((type) => `offer-builder-${type}` as const),
  ...OFFER_CONTENT_BLOCK_NAMES
] as const;

export type ContentBlockName = (typeof CONTENT_BLOCK_NAMES)[number];
export const isContentBlockName = (name: string): name is ContentBlockName =>
  CONTENT_BLOCK_NAMES.includes(name as any);

export const isOfferBuilderSection = (name: string | null | undefined) => {
  if (name && isContentBlockName(name)) {
    return (
      name === 'Offer Anchor' || name === 'Offer-list-builder' || name === 'Offer-price-builder'
    );
  }
  return false;
};

export const SlimContentBlockNames = ['Offer Anchor'] as const satisfies ContentBlockName[];

const componentMap = {
  'Expandable List': ExpandableList,
  'Image & Text': ImageAndText,
  'Image Gallery': ImageGallery,
  'List & Image': ListAndImage,
  'Offer-list-builder': OfferList,
  'Offer-price-builder': OfferPrice,
  'Offer Anchor': OfferAnchor,
  'offer-builder-bab': OfferBuildBundle
} as const satisfies Readonly<Record<ContentBlockName, ComponentType<ContentBlockProps>>>;

// <T> allows us to get the actual prop type of the chosen component.
export const getCustomComponent = <T extends ContentBlockName>(type: T) => componentMap[type];
